import { reactQueryGet } from "@circle-react/helpers/backendRequestHelpers";
import { internalApi } from "@circle-react/helpers/urlHelpers";
import type { Comment } from "@circle-react/types/Comment";
import { flatMap } from "lodash";
import uniqBy from "lodash/uniqBy";
import { useEffect, useEffectEvent } from "react";
import { useInfiniteQuery } from "react-query";

import { getCommentsListKey } from "./getCommentsListKey";

interface UseCommentsInfiniteQueryParams {
  onCommentsUpdate: (comments: Comment[]) => void;
  parentCommentId: string | number | null;
  postId: string | number;
  preloaded?: boolean;
  preloadedComments?: Comment[];
  preloadedCommentsTotalCount?: number;
  usedIn: string;
}

export const useCommentsInfiniteQuery = ({
  postId,
  parentCommentId,
  usedIn,
  preloaded = false,
  preloadedComments = [],
  preloadedCommentsTotalCount = 0,
  onCommentsUpdate,
}: UseCommentsInfiniteQueryParams) => {
  const query = useInfiniteQuery({
    initialPageParam: 1,
    queryKey: getCommentsListKey({
      postId,
      parentCommentId,
      usedIn,
    }),
    queryFn: ({ pageParam = 1 }) => {
      const pageParams = { per_page: 15, page: pageParam };
      const params = parentCommentId
        ? { parent_comment_id: parentCommentId, ...pageParams }
        : pageParams;

      return reactQueryGet(
        internalApi.comments.index({
          postId: postId,
          params,
        }),
      );
    },
    getNextPageParam: lastPage =>
      lastPage.has_next_page ? lastPage.page + 1 : undefined,
    initialData: () => {
      if (preloaded) {
        return {
          pages: [
            {
              records: preloadedComments,
              page: 0,
              has_next_page:
                preloadedCommentsTotalCount > preloadedComments.length,
            },
          ],
          pageParams: [],
        };
      }
      return undefined;
    },
    gcTime: 0,
    staleTime: Infinity,
  });

  const notifyCommentsUpdate = useEffectEvent((comments: Comment[]) => {
    onCommentsUpdate(comments);
  });

  useEffect(() => {
    if (!query.data) return;
    notifyCommentsUpdate(
      uniqBy(
        flatMap(query.data.pages, page => page.records),
        "id",
      ),
    );
  }, [query.data]);

  return query;
};
